
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Calendar, CheckCircle2, CalendarClock } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useAccessCodes } from "@/hooks/useAccessCodes";
import { useCheckOut } from "@/hooks/useCheckOut";
import { useBookingReview } from "@/hooks/useBookingReview";
import { useBookingReschedule } from "@/hooks/useBookingReschedule";
import AddReviewDialog from "./AddReviewDialog";
import { RescheduleDialog } from "@/components/booking/RescheduleDialog";
import type { Booking } from "@/hooks/useBookings";

interface BookingCardProps {
  booking: Booking;
  showActions?: boolean;
}

const BookingCard = ({ booking, showActions = false }: BookingCardProps) => {
  const navigate = useNavigate();
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  
  const { data: accessCodes } = useAccessCodes(booking.id);
  const checkOut = useCheckOut();
  const { data: existingReview, isLoading: reviewLoading } = useBookingReview(booking.id);
  const rescheduleBooking = useBookingReschedule();
  
  // Get the current active access code to determine if user has checked in
  const currentAccessCode = accessCodes?.[0];
  
  // Calculate session status with simplified logic
  const currentTime = new Date();
  const today = new Date().toISOString().split('T')[0];
  
  // FIXED: Create proper date objects for comparison
  // Parse the stored times which are now in local format (HH:MM format)
  const [startHours, startMinutes] = booking.start_time.split(':').map(Number);
  const [endHours, endMinutes] = booking.end_time.split(':').map(Number);
  
  const bookingDateTime = new Date(booking.booking_date);
  bookingDateTime.setHours(startHours, startMinutes, 0, 0);
  
  const sessionEnd = new Date(booking.booking_date);
  sessionEnd.setHours(endHours, endMinutes, 0, 0);

  // Check if booking is completed - only use database status, not time-based completion
  const statusIsCompleted = booking.status.toLowerCase() === 'completed';
  const isCancelled = booking.status.toLowerCase() === 'cancelled';
  // Don't mark as completed just because time has passed - only if actually completed in database
  const isCompleted = statusIsCompleted;

  // UPDATED LOGIC: Check for actual check-in status using database fields
  const hasCheckedInFromDatabase = booking.checked_in_at && !booking.checked_out_at;
  const isCheckedInFromNotes = !isCompleted && !isCancelled && 
    (booking.notes?.includes('[CHECKED IN') || booking.notes?.toLowerCase().includes('checked in'));
  
  // Customer is checked in if they have database check-in record OR notes indicating check-in OR if they have access codes
  const hasCheckedIn = hasCheckedInFromDatabase || isCheckedInFromNotes || !!currentAccessCode;
  
  // UPDATED: In Session is based on database check-in status primarily
  const isInSession = hasCheckedInFromDatabase || (hasCheckedIn && !isCompleted && !isCancelled);
  
  const sessionHasEnded = currentTime > sessionEnd;

  // Check if review already exists
  const hasExistingReview = !!existingReview;

  // Determine if we should show the review button
  const shouldShowReviewButton = isCompleted && showActions && !hasExistingReview && !reviewLoading;

  // Calculate reschedule availability (2 hours before start time)
  const rescheduleDeadline = new Date(bookingDateTime.getTime() - 2 * 60 * 60 * 1000);
  const canReschedule = !booking.status.toLowerCase().includes('cancelled') && 
                       !isCompleted && 
                       !hasCheckedIn && 
                       currentTime < rescheduleDeadline;

  console.log('BookingCard Status Debug:', {
    bookingId: booking.id,
    status: booking.status,
    statusIsCompleted,
    isCompleted,
    showActions,
    sessionHasEnded,
    hasCheckedIn,
    hasCheckedInFromDatabase,
    isCheckedInFromNotes,
    isInSession,
    hasExistingReview,
    reviewLoading,
    shouldShowReviewButton,
    currentTime: currentTime.toISOString(),
    sessionEnd: sessionEnd.toISOString(),
    bookingDateTime: bookingDateTime.toISOString(),
    notes: booking.notes,
    checked_in_at: booking.checked_in_at,
    checked_out_at: booking.checked_out_at,
    currentAccessCode: !!currentAccessCode
  });

  // FIXED: Format time without timezone conversion to prevent offset issues
  const formatBookingTime = (timeString: string) => {
    try {
      // Parse the time string directly without creating a Date object to avoid timezone issues
      const [hours, minutes] = timeString.split(':');
      const hourNum = parseInt(hours, 10);
      const minuteNum = parseInt(minutes, 10);
      
      // Create time string in 12-hour format
      const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
      const ampm = hourNum >= 12 ? 'PM' : 'AM';
      const minuteStr = minuteNum.toString().padStart(2, '0');
      
      return `${hour12}:${minuteStr} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return timeString; // Return original if parsing fails
    }
  };

  const formatBookingDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const getStatusBadge = () => {
    if (isCancelled) {
      return <Badge className="bg-red-100 text-red-700 border-red-200 whitespace-nowrap">Cancelled</Badge>;
    }
    if (isCompleted) {
      return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 whitespace-nowrap">Completed</Badge>;
    }
    // UPDATED: Show "In Session" when checked in but not checked out
    if (isInSession) {
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200 whitespace-nowrap">In Session</Badge>;
    }
    
    // Default to booking status (Confirmed or other)
    switch (booking.status.toLowerCase()) {
      case "confirmed":
        return <Badge className="bg-green-100 text-green-700 border-green-200 whitespace-nowrap">Confirmed</Badge>;
      default:
        return <Badge variant="secondary" className="whitespace-nowrap">{booking.status}</Badge>;
    }
  };

  const handleCheckOut = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await checkOut.mutateAsync(booking.id);
    } catch (error) {
      console.error('Check-out failed:', error);
    }
  };

  const handleViewDetails = () => {
    console.log('BookingCard: Navigating to booking details for booking ID:', booking.id);
    console.log('BookingCard: Navigation URL will be:', `/booking-details/${booking.id}`);
    
    // Prevent event bubbling that might interfere with navigation
    event?.preventDefault();
    event?.stopPropagation();
    
    try {
      navigate(`/booking-details/${booking.id}`);
      console.log('BookingCard: Navigation completed successfully');
    } catch (error) {
      console.error('BookingCard: Navigation failed:', error);
    }
  };

  const handleReschedule = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRescheduleDialogOpen(true);
  };

  const handleConfirmReschedule = async (newDate: string, newStartTime: string, newEndTime: string, timeSlotId?: string) => {
    try {
      await rescheduleBooking.mutateAsync({
        bookingId: booking.id,
        newDate,
        newStartTime,
        newEndTime,
        timeSlotId
      });
      setRescheduleDialogOpen(false);
    } catch (error) {
      console.error('Reschedule failed:', error);
    }
  };

  const canCheckOut = isInSession && !isCompleted && !isCancelled;

  // Convert booking to BookingDetail format for RescheduleDialog
  const bookingDetail = {
    id: booking.id,
    service_id: booking.service_id,
    service_name: booking.service_name || 'Unknown Service',
    booking_date: booking.booking_date,
    start_time: booking.start_time, // Display time (already converted to local)
    end_time: booking.end_time, // Display time (already converted to local)
    // TIMEZONE FIX: Add UTC times for backend operations
    // Note: In BookingCard, the booking object comes from useBookings which already converts to local time
    // We need to reconstruct UTC times from the booking data for consistency
    utc_start_time: booking.start_time, // This will be the display time, but needed for interface compatibility
    utc_end_time: booking.end_time, // This will be the display time, but needed for interface compatibility
    location: booking.location || 'Unknown Location',
    duration_minutes: booking.duration_minutes,
    status: booking.status,
    price: booking.price,
    currency: booking.currency || 'GBP',
    customer_name: booking.customer_name || '',
    customer_email: booking.customer_email || '',
    provider_id: booking.provider_id,
    user_id: booking.user_id
  };

  return (
    <>
      <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={handleViewDetails}>
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">{booking.service_name}</h3>
              <div className="flex items-center text-gray-600 mb-2">
                <MapPin className="h-4 w-4 mr-2" />
                <span className="text-sm">{booking.location}</span>
              </div>
              <div className="text-sm text-gray-500">{booking.address}</div>
            </div>
            {getStatusBadge()}
          </div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              <span className="text-sm">{formatBookingDate(booking.booking_date)}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Clock className="h-4 w-4 mr-2" />
              <span className="text-sm">
                {formatBookingTime(booking.start_time)} - {formatBookingTime(booking.end_time)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-gray-900">
              £{booking.price.toFixed(2)}
            </div>
            
            {showActions && (
              <div className="flex gap-2">
                {canCheckOut && (
                  <Button
                    onClick={handleCheckOut}
                    disabled={checkOut.isPending}
                    size="sm"
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    {checkOut.isPending ? "Checking Out..." : "Check Out"}
                  </Button>
                )}
                
                {!isCompleted && (
                  <>
                    {canReschedule && (
                      <Button
                        onClick={handleReschedule}
                        disabled={rescheduleBooking.isPending}
                        variant="outline"
                        size="sm"
                      >
                        <CalendarClock className="h-4 w-4 mr-1" />
                        {rescheduleBooking.isPending ? "Processing..." : "Reschedule"}
                      </Button>
                    )}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleViewDetails();
                      }}
                      variant="default"
                      size="sm"
                    >
                      View Details
                    </Button>
                  </>
                )}
                
                {isCompleted && (
                  <div className="flex items-center gap-2">
                    {shouldShowReviewButton && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <AddReviewDialog
                          bookingId={booking.id}
                          providerName={booking.location || "this provider"}
                          onReviewAdded={() => {
                            console.log('Review added successfully for booking:', booking.id);
                          }}
                        />
                      </div>
                    )}
                    
                    <Button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleViewDetails();
                      }}
                      variant="default"
                      size="sm"
                    >
                      View Details
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reschedule Dialog */}
      <RescheduleDialog
        open={rescheduleDialogOpen}
        onOpenChange={setRescheduleDialogOpen}
        booking={bookingDetail}
        onReschedule={handleConfirmReschedule}
        isLoading={rescheduleBooking.isPending}
      />
    </>
  );
};

export default BookingCard;
